import type { Map as MapboxMap } from 'mapbox-gl';
import {
  CUSTOM_DATA_LAYER_PREFIX,
  customDataLayerId,
  parseCustomDataCategoryLeafId,
} from '~/pages/projectDetails/map/customDataLayers.helpers';
import type { IdentifyDetailsSectionsCache } from '../..';
import { isFeatureStateGeometry } from '../featureStateHighlight.helpers';
import type { FeatureRef, SidebarSection } from './buildSidebarSections';

// Re-exported from the canonical definition in IdentifyDetails/common.
export { calcBoundingBox } from '../IdentifyDetails/common';

// The layer ids the metadata identify queries: active layers plus currently-visible custom-data
// layers (and their -fill/-circle sublayers). Custom-data line layers are deliberately kept out of
// activeLayerIds (MapLayers owns that set), so they're unioned in here at read-time. Every id is
// guarded to layers present in the current style — queryRenderedFeatures throws on any unknown id.
export function getIdentifiableLayerIds(
  map: Pick<MapboxMap, 'getLayer'>,
  layers: { visibleLayerIds: ReadonlySet<string>; activeLayerIds: readonly string[] },
): string[] {
  // A split row's visible ids are per-category leaves, which are not real mapbox layers — collapse
  // them back to the row's layer id (deduped) or click-to-inspect would drop those rows entirely.
  // Hidden categories still fall out naturally: queryRenderedFeatures respects the layer filters.
  const customDataRowIds = new Set(
    Array.from(layers.visibleLayerIds)
      .filter((id) => id.startsWith(CUSTOM_DATA_LAYER_PREFIX))
      .map((id) => {
        const parsed = parseCustomDataCategoryLeafId(id);
        return parsed ? customDataLayerId(parsed.rowId) : id;
      }),
  );
  const customDataQueryIds = Array.from(customDataRowIds)
    .flatMap((id) => [id, `${id}-fill`, `${id}-circle`])
    .filter((id) => Boolean(map.getLayer(id)));
  return [...layers.activeLayerIds.filter((id) => Boolean(map.getLayer(id))), ...customDataQueryIds];
}

// Chooses which sections the sidebar renders and its header chip label from the click mode.
// No key => area mode (right-click / custom-data): show every section, "N in area".
// Key with a matching section => single-select (left-click): show just that one, "1 selected".
// Key with no match (cross-query drift / stale key) => fall back to area mode so the panel is
// never blanked while sections exist.
export function selectVisibleSections(
  sections: SidebarSection[],
  selectedFeatureKey: string | undefined,
): { visible: SidebarSection[]; chipLabel: string } {
  const areaLabel = `${sections.length} in area`;
  if (!selectedFeatureKey) return { visible: sections, chipLabel: areaLabel };

  const match = sections.find((s) => s.id === selectedFeatureKey);
  if (match) return { visible: [match], chipLabel: '1 selected' };
  return { visible: sections, chipLabel: areaLabel };
}

/** Shown in the empty sidebar and announced whenever an identify finds nothing. */
export const NO_FEATURES_FOUND_MESSAGE = 'No features found at this location.';

const MAX_ANNOUNCED_LAYERS = 3;

/**
 * Screen-reader text for an identify result, built from the same sections the sidebar shows.
 * Single-select names the feature's layer; area mode counts features and groups repeats by layer
 * ("5 features found: Water Mains (3), Gas Lines (2)"), naming at most three layers.
 */
export function describeSelection(sections: SidebarSection[], selectedFeatureKey: string | undefined): string {
  const { visible } = selectVisibleSections(sections, selectedFeatureKey);
  if (selectedFeatureKey && visible[0]?.id === selectedFeatureKey) return `Selected ${visible[0].title}`;
  if (visible.length === 0) return NO_FEATURES_FOUND_MESSAGE;

  const countsByTitle = new Map<string, number>();
  for (const section of visible) countsByTitle.set(section.title, (countsByTitle.get(section.title) ?? 0) + 1);
  const layers = [...countsByTitle].map(([title, count]) => (count > 1 ? `${title} (${count})` : title));

  const named = layers.slice(0, MAX_ANNOUNCED_LAYERS).join(', ');
  const remaining = layers.length - MAX_ANNOUNCED_LAYERS;
  const more = remaining > 0 ? `, and ${remaining} more ${remaining === 1 ? 'layer' : 'layers'}` : '';
  const noun = visible.length === 1 ? 'feature' : 'features';
  return `${visible.length} ${noun} found: ${named}${more}`;
}

// Bridges the closing-panel remount race (Project Info, Downloads, Proposal Packet — CG-850):
// MetadataSidebar can mount and run its identify query before MapLayers re-registers layers, so
// `mapReady` (queryLayerIds.length > 0) tells whether the query ran against a real layer set. When
// it didn't, render the last cached sections for the same point instead of the race's
// authoritative-looking empty result. The cache must also match the current projectId, so a
// Project A -> B remount never renders A's sections.
export function resolveSidebarSections(params: {
  mapReady: boolean;
  liveSections: SidebarSection[];
  point: { lng: number; lat: number } | undefined;
  cache: IdentifyDetailsSectionsCache | undefined;
  projectId: string | undefined;
}): SidebarSection[] {
  const { mapReady, liveSections, point, cache, projectId } = params;
  if (mapReady) return liveSections;
  if (
    point &&
    cache &&
    // An unresolved projectId must never match a cached one: the previous `projectId ?? ''` made two
    // separate unknown contexts both '' and compare equal (CG-920).
    projectId &&
    cache.projectId === projectId &&
    cache.point.lng === point.lng &&
    cache.point.lat === point.lat
  ) {
    return cache.sections;
  }
  return liveSections;
}

// The highlight target: geometry + the feature's own (utility) color + the live vector-tile
// reference used for feature-state highlighting. Hovered card wins; else the left-click selected
// feature persists; else nothing (area mode / stale key).
export function selectHighlightTarget(
  sections: SidebarSection[],
  selectedFeatureKey: string | undefined,
  hovered: { geometry: GeoJSON.Geometry; color?: string; featureRef?: FeatureRef } | null,
): { geometry: GeoJSON.Geometry; color?: string; featureRef?: FeatureRef } | null {
  if (hovered) return hovered;
  if (!selectedFeatureKey) return null;
  const match = sections.find((s) => s.id === selectedFeatureKey);
  if (!match?.geometry) return null;
  return {
    geometry: match.geometry,
    color: match.coreColor,
    ...(match.featureRef ? { featureRef: match.featureRef } : {}),
  };
}

// Which highlight target takes the whole-feature (feature-state) path, and which stays on the
// tile-clipped GeoJSON fragment. Returns null for the GeoJSON path. The point exclusion lives in
// isFeatureStateGeometry, shared with the source list the layer stacks are mounted from.
export function selectFeatureStateTarget(
  target: { geometry: GeoJSON.Geometry; color?: string; featureRef?: FeatureRef } | null,
): { featureRef: FeatureRef; color?: string } | null {
  if (!target?.featureRef) return null;
  if (!isFeatureStateGeometry(target.geometry)) return null;
  return { featureRef: target.featureRef, color: target.color };
}

// Project-boundary click priority: a left-click over a project boundary defers to the boundary's own
// click handler (opens the project card) instead of opening the metadata sidebar. Matches the current
// org's `project-polygon` layer and delegated/cross-org `project-polygon-${orgId}` layers.
export function hitsProjectBoundary(layerIds: (string | undefined)[]): boolean {
  return layerIds.some((id) => Boolean(id?.startsWith('project-polygon')));
}

// A usable identify point: present with finite lng AND lat.
export function hasValidIdentifyPoint(point: { lng: number; lat: number } | null | undefined): boolean {
  return !!point && Number.isFinite(point.lng) && Number.isFinite(point.lat);
}

// Whether the persist effect should write: map ready, point valid, and sections worth keeping.
// mapReady means layers are registered, not painted, so a ready-but-unpainted pass can return
// empty — caching that would clobber a good cache and leave the fallback empty (CG-850, CG-920).
export function shouldPersistCache({
  mapReady,
  point,
  sections,
}: {
  mapReady: boolean;
  point: { lng: number; lat: number } | undefined;
  sections: SidebarSection[];
}): boolean {
  return mapReady && hasValidIdentifyPoint(point) && sections.length > 0;
}

// Whether the docked metadata sidebar is open. `showLayerDetails`/`isStyleLoaded` default true for
// callers that can't see them (e.g. AgenciesSummary).
export function isMetadataSidebarOpen(
  point: { lng: number; lat: number } | null | undefined,
  { showLayerDetails = true, isStyleLoaded = true }: { showLayerDetails?: boolean; isStyleLoaded?: boolean } = {},
): boolean {
  return showLayerDetails && isStyleLoaded && hasValidIdentifyPoint(point);
}
